import Anthropic from '@anthropic-ai/sdk';

/**
 * AI Model Integration Utilities
 * This file contains functions for integrating with various AI models.
 * Currently supports:
 * - Anthropic (Claude)
 * - Static predefined responses
 */

// Available AI models configuration
export const availableModels = [
  { 
    id: 'claude-3-opus', 
    name: 'Claude 3 Opus', 
    provider: 'anthropic',
    description: 'Most capable model for complex tasks',
    modelIdentifier: 'claude-3-opus-20240229'
  },
  { 
    id: 'claude-3-sonnet', 
    name: 'Claude 3 Sonnet', 
    provider: 'anthropic',
    description: 'Balanced performance and efficiency',
    modelIdentifier: 'claude-3-sonnet-20240229'
  },
  { 
    id: 'claude-3-haiku', 
    name: 'Claude 3 Haiku', 
    provider: 'anthropic',
    description: 'Fastest model for simple queries',
    modelIdentifier: 'claude-3-haiku-20240307'
  }
];

/**
 * Loads portfolio content from localStorage
 * @returns {Object} Portfolio content sections
 */
const getPortfolioContent = () => {
  try {
    const portfolioData = localStorage.getItem('portfolioData');
    if (!portfolioData) {
      return {
        about: '',
        skills: '',
        projects: '',
        experience: ''
      };
    }
    
    const data = JSON.parse(portfolioData);
    return {
      about: data.about?.description || '',
      skills: data.skills?.map(skill => `${skill.name}: ${skill.description}`).join('\n') || '',
      projects: data.projects?.map(project => `${project.title}: ${project.description}`).join('\n') || '',
      experience: data.experience?.map(exp => `${exp.title} at ${exp.company}: ${exp.description}`).join('\n') || ''
    };
  } catch (error) {
    console.error('Error loading portfolio content:', error);
    return {
      about: '',
      skills: '',
      projects: '',
      experience: ''
    };
  }
};

/**
 * Gets an AI response from the selected model
 * @param {string} prompt - The user's message or prompt
 * @param {object} options - Configuration options including model, settings, etc.
 * @returns {Promise<string>} - The AI's response text
 */
export const getAIResponse = async (prompt, options = {}) => {
  const {
    modelId = 'claude-3-sonnet',
    systemPrompt = "You are an AI assistant for Dhiraj T H, an AI Engineer and Innovator. Provide helpful, concise responses about Dhiraj's work, expertise, and services.",
    temperature = 0.7,
    maxTokens = 500,
    anthropicKey = '',
    customResponses = {},
  } = options;

  // First, check if there's a matching custom response
  const lowercasePrompt = prompt.toLowerCase();
  for (const [keyword, response] of Object.entries(customResponses)) {
    if (lowercasePrompt.includes(keyword.toLowerCase())) {
      return response;
    }
  }

  // Get portfolio content
  const portfolioContent = getPortfolioContent();
  
  // Create enhanced system prompt with portfolio content
  const enhancedSystemPrompt = `${systemPrompt}

Here is the current portfolio content to reference:

About:
${portfolioContent.about}

Skills:
${portfolioContent.skills}

Projects:
${portfolioContent.projects}

Experience:
${portfolioContent.experience}

Please use this information to provide accurate and up-to-date responses about Dhiraj's work and experience.`;

  // Based on selected model, choose the appropriate API
  switch (modelId) {
    case 'claude-3-opus':
    case 'claude-3-sonnet':
    case 'claude-3-haiku':
      if (!anthropicKey) {
        return "The Claude API key is not configured. Please contact the administrator to set up the API key.";
      }
      try {
        return await callAnthropic(prompt, { 
          systemPrompt: enhancedSystemPrompt, 
          temperature, 
          maxTokens, 
          apiKey: anthropicKey,
          modelId
        });
      } catch (error) {
        console.error('Error in getAIResponse:', error);
        return error.message || "Failed to get response from Claude API. Please try again later.";
      }
      
    case 'static':
    default:
      return "I don't have specific information about that. Would you like me to connect you with Dhiraj directly through the contact form?";
  }
};

/**
 * Call Anthropic API with the provided parameters using the official SDK
 * @private
 */
const callAnthropic = async (prompt, options) => {
  try {
    const { apiKey, systemPrompt, temperature, maxTokens, modelId } = options;
    
    if (!apiKey) {
      throw new Error("API key is required");
    }

    // Initialize the Anthropic client
    const anthropic = new Anthropic({
      apiKey: apiKey,
    });

    // Get the model identifier
    const model = availableModels.find(m => m.id === modelId)?.modelIdentifier;
    if (!model) {
      throw new Error(`Invalid model ID: ${modelId}`);
    }

    // Create the message
    const msg = await anthropic.messages.create({
      model: model,
      max_tokens: maxTokens,
      temperature: temperature,
      system: systemPrompt,
      messages: [
        {
          role: "user",
          content: prompt
        }
      ]
    });

    if (!msg.content || !msg.content[0]?.text) {
      throw new Error("Invalid response format from Claude API");
    }

    // Return the response text
    return msg.content[0].text;
  } catch (error) {
    console.error('Error calling Anthropic:', error);
    if (error.message.includes('API key')) {
      throw new Error("The Claude API key is not configured correctly. Please contact the administrator.");
    }
    if (error.message.includes('rate limit')) {
      throw new Error("Rate limit exceeded. Please try again in a few moments.");
    }
    if (error.message.includes('invalid model')) {
      throw new Error("Invalid model configuration. Please contact the administrator.");
    }
    throw new Error(error.message || "I encountered an error while processing your request. Please try again later.");
  }
};

/**
 * Implementation for OpenAI API calls
 * Uncomment and use in production
 */
/*
const callOpenAI = async (prompt, model, options) => {
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  
  if (!apiKey) {
    throw new Error('OpenAI API key not found');
  }
  
  const response = await fetch(model.apiEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: model.modelIdentifier,
      messages: [
        {
          role: 'system',
          content: options.systemPrompt || "You are an AI assistant for Dhiraj T H, an AI Engineer and Innovator. Provide helpful, concise responses about Dhiraj's work, expertise, and services."
        },
        {
          role: 'user',
          content: prompt
        }
      ],
      temperature: options.temperature || 0.7,
      max_tokens: options.maxTokens || 500
    })
  });
  
  const data = await response.json();
  
  if (data.error) {
    throw new Error(`OpenAI API error: ${data.error.message}`);
  }
  
  return data.choices[0].message.content;
};
*/

/**
 * Implementation for Anthropic API calls
 * Uncomment and use in production
 */
/*
const callAnthropic = async (prompt, model, options) => {
  const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
  
  if (!apiKey) {
    throw new Error('Anthropic API key not found');
  }
  
  const response = await fetch(model.apiEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01'
    },
    body: JSON.stringify({
      model: model.modelIdentifier,
      system: options.systemPrompt || "You are an AI assistant for Dhiraj T H, an AI Engineer and Innovator. Provide helpful, concise responses about Dhiraj's work, expertise, and services.",
      messages: [
        {
          role: 'user',
          content: prompt
        }
      ],
      temperature: options.temperature || 0.7,
      max_tokens: options.maxTokens || 500
    })
  });
  
  const data = await response.json();
  
  if (data.error) {
    throw new Error(`Anthropic API error: ${data.error.message}`);
  }
  
  return data.content[0].text;
};
*/ 